import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

interface ActivityDataTableProps {
  aggregatedSearchData: Array<{ label: string; count: number }>;
  aggregatedClickData: Array<{ label: string; count: number }>;
  searchTotal: number;
  clicksTotal: number;
}

export const ActivityDataTable: React.FC<ActivityDataTableProps> = ({
  aggregatedSearchData,
  aggregatedClickData,
  searchTotal,
  clicksTotal,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const tableData = aggregatedSearchData.map((item, index) => ({
    period: item.label,
    searches: item.count,
    clicks: aggregatedClickData[index]?.count || 0,
  }));

  return (
    <Box
      aria-label={String(
        t('statistics.activity.tableRegion', 'Activity statistics table')
      )}
      tabIndex={0}
      sx={{
        mt: 24,
        maxHeight: 400,
        overflowY: 'auto',
        overflowX: 'hidden',
        border: '1px solid var(--color-greyish-blue)',
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
      }}
    >
      <Table
        stickyHeader
        size="small"
        aria-label={String(
          t(
            'statistics.activity.tableLabel',
            'Activity data showing searches and clicks by period'
          )
        )}
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& th, & td': {
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          },
          '& thead th': {
            fontSize: { xs: '12px', sm: '14px' },
            lineHeight: 1.2,
            px: { xs: 4, sm: 16 },
            py: { xs: 2, sm: 6 },
          },
          '& tbody td': {
            fontSize: { xs: '12px', sm: '14px' },
            lineHeight: 1.2,
            px: { xs: 4, sm: 16 },
            py: { xs: 2, sm: 6 },
          },
        }}
      >
        <caption className="sr-only">
          {t(
            'statistics.activity.tableCaption',
            `Activity data from ${aggregatedSearchData[0]?.label || ''} to ${
              aggregatedSearchData[aggregatedSearchData.length - 1]?.label || ''
            }. 
                Total: ${searchTotal} searches, ${clicksTotal} clicks.`
          )}
        </caption>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '35%' }}>
              {t('statistics.table.period', 'Period')}
            </TableCell>
            <TableCell
              align="right"
              sx={{ color: theme.palette.primary.main }}
              aria-sort="none"
            >
              {t('statistics.legend.searches', 'Searches')}
            </TableCell>
            <TableCell
              align="right"
              sx={{ color: theme.palette.warning.main }}
              aria-sort="none"
            >
              {t('statistics.legend.journalClicks', 'Clicks')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, index) => (
            <TableRow key={row.period} aria-rowindex={index + 2}>
              <TableCell
                component="th"
                scope="row"
                id={`period-${index}`}
                sx={{ width: '35%' }}
              >
                {row.period}
              </TableCell>
              <TableCell
                align="right"
                headers={`period-${index}`}
                aria-label={`${row.searches.toLocaleString()} searches`}
              >
                {row.searches.toLocaleString()}
              </TableCell>
              <TableCell
                align="right"
                headers={`period-${index}`}
                aria-label={`${row.clicks.toLocaleString()} clicks`}
              >
                {row.clicks.toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
          <TableRow
            aria-rowindex={tableData.length + 2}
            sx={{
              backgroundColor: 'var(--color-greyish-blue)',
              '&:hover': {
                backgroundColor: 'var(--color-greyish-blue)',
              },
              '& td, & th': { fontWeight: 600 },
            }}
          >
            <TableCell component="th" scope="row" id="total-row">
              {t('statistics.table.total', 'Total')}
            </TableCell>
            <TableCell
              align="right"
              headers="total-row"
              aria-label={`Total: ${searchTotal.toLocaleString()} searches`}
            >
              {searchTotal.toLocaleString()}
            </TableCell>
            <TableCell
              align="right"
              headers="total-row"
              aria-label={`Total: ${clicksTotal.toLocaleString()} clicks`}
            >
              {clicksTotal.toLocaleString()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};
